"use client";

import { useState } from "react";
import Link from "next/link";
import { storyblokEditable } from "@storyblok/react/rsc";

import { trackEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { GridContainer } from "@/components/layout/GridContainer";
import { RichTextField } from "@/components/typography/RichTextField";
import { ArrowRight } from "@/components/icons/ArrowRight";
import { SignupModal } from "@/components/layout/SignupModal";
import { MembershipPaymentModal } from "@/components/membership/MembershipPaymentModal";
import { getUrlFromLinkObject } from "@/lib/storyblok/utils";
import type { MultilinkStoryblok } from "@/component-types-sb";

type CtaSectionStoryblok = {
  title?: string;
  text?: string;
  cta_label?: string;
  cta_link?: MultilinkStoryblok;
  cta_type?: string;
  _uid: string;
  component: "cta_section";
  [k: string]: any;
};

type CtaSectionProps = {
  blok: CtaSectionStoryblok;
  inline?: boolean;
};

const CtaSection = ({ blok, inline }: CtaSectionProps) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const ctaHref = getUrlFromLinkObject(blok.cta_link);
  const ctaType = blok.cta_type || "link";
  const checkoutEnabled = process.env.NEXT_PUBLIC_CHECKOUT_ENABLED === "true";
  const isMembershipPayment = ctaType === "membership_payment" && checkoutEnabled;
  const signupTopic = ctaType !== "link" && ctaType !== "membership_payment" ? ctaType : undefined;
  const hasCta = blok.cta_label && (ctaHref || signupTopic || isMembershipPayment);

  /**
   * Die drei Varianten dieses Blocks feuerten bisher gar nichts — auf den
   * Lead-Seiten endete die Spur damit beim `lead_pageview`, und ob der
   * Mitglieder-CTA ueberhaupt geklickt wird, stand nirgends.
   *
   * `variant` trennt die drei Wege: `membership_payment` oeffnet den
   * Checkout, ein `signupTopic` das Anmeldemodal, `link` navigiert weg.
   * Ohne das waeren die sehr unterschiedlichen Absichten eine Zahl.
   *
   * UTMs kommen mit, weil derselbe Block auf Lead-Seiten steht, deren
   * Kampagnen-Herkunft sonst nur am `lead_pageview` haengt und beim Klick
   * verloren ginge.
   */
  const trackCtaClick = (variant: "membership_payment" | "signup" | "link") => {
    const params =
      typeof window !== "undefined"
        ? new URLSearchParams(window.location.search)
        : null;

    trackEvent("cta_section_clicked", {
      variant,
      // Derselbe Block steht als eigener Abschnitt auf Lead- und
      // Content-Seiten *und* inline mitten im Artikeltext (Article.tsx).
      // Das sind verschiedene Platzierungen mit verschiedenen Erwartungen —
      // ohne dieses Feld waeren sie in einer Zahl vermischt.
      placement: inline ? "article_inline" : "section",
      label: blok.cta_label ?? null,
      title: blok.title ?? null,
      signup_topic: signupTopic ?? null,
      href: ctaHref || null,
      // Erlaubt "welche Lead-Seite konvertiert?", ohne die URL zu parsen.
      page_path: typeof window !== "undefined" ? window.location.pathname : null,
      utm_source: params?.get("utm_source") ?? null,
      utm_campaign: params?.get("utm_campaign") ?? null,
      utm_content: params?.get("utm_content") ?? null,
      utm_medium: params?.get("utm_medium") ?? null,
    });
  };

  const ctaContent = (
    <div className="bg-sky-75 rounded-3xl p-6 flex flex-col gap-6 items-center w-full">
      {blok.title && (
        <h2 className="text-headline-xl text-black text-center">
          {blok.title}
        </h2>
      )}
      {blok.text && (
        <RichTextField value={blok.text} className="text-copy text-black text-center" />
      )}
      {hasCta && isMembershipPayment && (
        <Button
          variant="sun"
          className="w-full md:w-auto"
          onClick={() => {
            trackCtaClick("membership_payment");
            setPaymentModalOpen(true);
          }}
        >
          {blok.cta_label}
          <ArrowRight />
        </Button>
      )}
      {hasCta && signupTopic && (
        <Button
          variant="sun"
          className="w-full md:w-auto"
          onClick={() => {
            trackCtaClick("signup");
            setModalOpen(true);
          }}
        >
          {blok.cta_label}
          <ArrowRight />
        </Button>
      )}
      {hasCta && !signupTopic && !isMembershipPayment && ctaHref && (
        <Button variant="sun" className="w-full md:w-auto" asChild>
          {/* Navigation: `trackEvent` ist fire-and-forget mit `keepalive`,
              der Versand ueberlebt den Seitenwechsel. */}
          <Link href={ctaHref} onClick={() => trackCtaClick("link")}>
            {blok.cta_label}
            <ArrowRight />
          </Link>
        </Button>
      )}
    </div>
  );

  const modals = (
    <>
      {signupTopic && (
        <SignupModal
          open={modalOpen}
          onOpenChange={setModalOpen}
          signupTopic={signupTopic}
          buttonLabel={blok.cta_label}
        />
      )}
      {isMembershipPayment && (
        <MembershipPaymentModal
          open={paymentModalOpen}
          onOpenChange={setPaymentModalOpen}
          source="cta_section"
        />
      )}
    </>
  );

  if (inline) {
    return (
      <div className="my-8" {...storyblokEditable(blok)}>
        {ctaContent}
        {modals}
      </div>
    );
  }

  return (
    <GridContainer
      {...storyblokEditable(blok)}
      className="py-12 !px-0 md:!px-8"
    >
      <div className="col-span-12 md:col-span-8 md:col-start-3 lg:col-span-8 lg:col-start-3 flex flex-col items-center">
        <div className="w-full px-4 md:px-0">
          {ctaContent}
        </div>
      </div>
      {modals}
    </GridContainer>
  );
};

export default CtaSection;
